import { useEffect, useMemo, useRef, useState } from 'react';
import { useConfigService } from '@thd-nucleus/experience-context';

import { getItemQuantities } from '@thd-olt-component-react/product-bundle';
import {
  groupLaundryProductsByType,
  getUpdatedLaundryBundle,
  getBundleAccessories,
  getBundleDetails,
  getAccessoryProductIds,
  updateLaundryProducts,
  getLaundryDefaultProductIds
} from '../utils/product-utils';
import { useDataProducts } from './useDataProducts';
import { NONE_VALUE } from '../constants/index';

export const useLaundryEntryPoint = ({ bundle }) => {
  const isLaundryEntryPointEnabled = useConfigService('fs:isLaundryEntryPointEnabled') || false;
  const isAccessoriesFeatureEnabled = useConfigService('fs:isAccessoriesFeatureEnabled') || false;
  const configBundleProductsLaundry = useConfigService('bundleProductsLaundry') || null;
  const bundleProductsLaundry = configBundleProductsLaundry ? configBundleProductsLaundry.split(',') : null;
  const isVariantSwap = useRef(false);
  // Selected accessory offering, applied when the next fetch resolves.
  const selectedVariantRef = useRef(NONE_VALUE);
  // After a swap the parent bundle's rolled-up adjustments are stale; this tells the
  // pricing hook to recompute the total from the fresh per-item adjustments.
  const [isBundleModified, setIsBundleModified] = useState(false);
  const [productsState, setProductsState] = useState(null);
  const [productIds, setProductIds] = useState(null);

  const updatedBundle = useMemo(
    () => getUpdatedLaundryBundle(bundle, isAccessoriesFeatureEnabled),
    [bundle, isAccessoriesFeatureEnabled]
  );

  // Derived once; deliberately not recomputed when the bundle changes.
  const bundleDetailsRef = useRef(null);
  if (bundleDetailsRef.current === null) {
    bundleDetailsRef.current = getBundleDetails(updatedBundle?.bundleSpecificationDetails);
  }

  const bundleAccessories = useMemo(
    () => getBundleAccessories(updatedBundle?.bundleSpecificationDetails),
    [updatedBundle]
  );

  const defaultProductIds = useMemo(
    () => getLaundryDefaultProductIds({
      components: updatedBundle?.bundleSpecificationDetails?.components,
      accessories: bundleAccessories
    }),
    [updatedBundle, bundleAccessories]
  );
  const itemIds = bundleProductsLaundry || productIds || defaultProductIds;
  const skip = !isLaundryEntryPointEnabled || !itemIds || itemIds.length === 0;

  const activeProductItemIds = (productsState?.products || []).map((product) => product.itemId);
  const selectedAccessoryProducts = productsState?.accessories?.selectedVariant !== NONE_VALUE
    ? (productsState?.accessories?.products || [])
    : [];

  const selectedOfferingName = productsState?.accessories?.selectedVariant ?? NONE_VALUE;
  const selectedAccessorySpecs = selectedOfferingName !== NONE_VALUE
    ? (bundleAccessories?.find((offering) => offering.offeringName === selectedOfferingName)?.products || [])
    : [];

  const { data, loading, error } = useDataProducts({
    itemIds,
    itemQuantities: getItemQuantities(itemIds, updatedBundle, null, selectedAccessorySpecs),
    skip
  });

  // Every change re-fetches the full selected composition (products + active accessory) so the
  // backend recomputes each item's promotions in the new bundle context.
  const swapProduct = ({ currentItemId, newItemId }) => {
    if (!activeProductItemIds.includes(currentItemId)) {
      return;
    }
    isVariantSwap.current = true;
    setIsBundleModified(true);
    const newProductIds = activeProductItemIds.map((id) => (id === currentItemId ? newItemId : id));
    const accessoryIds = selectedAccessoryProducts.map((product) => product.itemId);
    setProductIds([...newProductIds, ...accessoryIds]);
  };

  // Optimistic update; the fetch effect refines it if the composition changes.
  const selectAccessoryVariant = (offeringName) => {
    selectedVariantRef.current = offeringName;
    setProductsState((prev) => (prev
      ? { ...prev, accessories: { ...prev.accessories, selectedVariant: offeringName } }
      : prev));
  };

  const swapAccessory = ({ offeringName }) => {
    isVariantSwap.current = true;
    setIsBundleModified(true);
    if (offeringName === NONE_VALUE) {
      // Deselecting keeps the last accessory visible but excludes it from the fetch and the total.
      selectAccessoryVariant(NONE_VALUE);
      setProductIds([...activeProductItemIds]);
      return;
    }
    const accessoryOffering = bundleAccessories.find((offering) => offering.offeringName === offeringName);
    if (!accessoryOffering) {
      return;
    }
    selectAccessoryVariant(offeringName);
    const accessoryIds = accessoryOffering?.products?.map(({ defaultProductId }) => defaultProductId) || [];
    setProductIds([...activeProductItemIds, ...accessoryIds]);
  };

  useEffect(() => {
    if (!data?.products) {
      return;
    }
    try {
      const updatedProducts = updateLaundryProducts({
        products: data?.products,
        bundleDetails: bundleDetailsRef.current,
        selectedAccessory: selectedVariantRef.current
      });
      const grouped = groupLaundryProductsByType(updatedProducts, bundleDetailsRef.current);
      setProductsState((prev) => ({
        products: grouped.products,
        accessories: {
          ...grouped.accessories,
          // Keep the last fetched accessory products so the option stays visible after deselecting.
          products: grouped.accessories.products.length
            ? grouped.accessories.products
            : (prev?.accessories?.products || []),
          selectedVariant: selectedVariantRef.current
        }
      }));
    } catch (errorProducts) {
      setProductsState({});
    }

  }, [data]);

  const { products, accessories } = productsState || {};
  return {
    products: products || [],
    accessories: accessories || [],
    error,
    loading: !skip && (loading || productsState == null) && !error && !isVariantSwap?.current,
    isBundleModified,

    // methods
    swapProduct,
    swapAccessory
  };
};